<template>
  <section class="faqbox" dir="rtl">
    <header class="faqbox__head">
      <h3>{{ L.title }} <small>({{ items.length }})</small></h3>
      <span class="faqbox__hint">{{ L.hint }}</span>
    </header>
    <div class="faqbox__body">
      <div v-for="(f, i) in items" :key="i" class="faqbox__item">
        <div class="faqbox__bar">
          <span class="faqbox__num">#{{ i + 1 }}</span>
          <span>
            <button type="button" class="faqbox__icon" :disabled="i === 0" @click="move(i, -1)">&#9650;</button>
            <button type="button" class="faqbox__icon" :disabled="i === items.length - 1" @click="move(i, 1)">&#9660;</button>
            <button type="button" class="faqbox__icon faqbox__icon--del" :title="L.del" @click="items.splice(i, 1)">&#10005;</button>
          </span>
        </div>
        <input v-model="f.q" class="form-control faqbox__input" maxlength="500" :placeholder="L.q">
        <textarea v-model="f.a" class="form-control faqbox__input" rows="3" maxlength="5000" :placeholder="L.a"/>
      </div>
      <button v-if="items.length < 50" type="button" class="faqbox__btn" @click="items.push({ q: '', a: '' })">+ {{ L.add }}</button>
    </div>
  </section>
</template>

<script>
/**
 * Visible FAQ (Q/A pairs) per entity: stored in the API entity_faq table (/api/admin/faq/get|save),
 * rendered on the storefront under "FAQ" with FAQPage JSON-LD. Parent forms call save(id) after their own save.
 */
import axios from "@/api/http";

export default {
  name: "FaqBox",
  props: {
    entityType: { type: String, required: true },
    entityId: { type: [Number, String], default: 0 },
  },
  data() {
    return {
      items: [],
      L: {
        title: "\u0633\u0648\u0627\u0644\u0627\u062a \u0645\u062a\u062f\u0627\u0648\u0644 (FAQ)",
        hint: "\u0646\u0645\u0627\u06cc\u0634 \u062f\u0631 \u0633\u0627\u06cc\u062a + \u0627\u0633\u06a9\u06cc\u0645\u0627\u06cc FAQPage",
        q: "\u0633\u0648\u0627\u0644",
        a: "\u067e\u0627\u0633\u062e",
        add: "\u0627\u0641\u0632\u0648\u062f\u0646 \u0633\u0648\u0627\u0644",
        del: "\u062d\u0630\u0641",
        err: "\u062e\u0637\u0627 \u062f\u0631 \u0630\u062e\u06cc\u0631\u0647 \u0633\u0648\u0627\u0644\u0627\u062a \u0645\u062a\u062f\u0627\u0648\u0644",
      },
    };
  },
  watch: {
    entityId: { immediate: true, handler(v) { this.load(v); } },
  },
  methods: {
    move(i, d) {
      const j = i + d;
      if (j < 0 || j >= this.items.length) return;
      const a = this.items.slice();
      [a[i], a[j]] = [a[j], a[i]];
      this.items = a;
    },
    load(id) {
      id = Number(id) || 0;
      if (!id) { this.items = []; return; }
      axios.post("/api/admin/faq/get", { entity_type: this.entityType, entity_id: id }, { show_pros: false }).then((res) => {
        const list = (res.data && res.data.items) || [];
        this.items = list.map((f) => ({ q: f.q || "", a: f.a || "" }));
      }).catch(() => {});
    },
    /** Persist the FAQ for the given entity id (call after the parent form saved successfully). */
    save(id) {
      id = Number(id) || Number(this.entityId) || 0;
      if (!id) return Promise.resolve(null);
      const items = this.items.map((f) => ({ q: (f.q || "").trim(), a: (f.a || "").trim() })).filter((f) => f.q && f.a);
      return axios.post("/api/admin/faq/save", { entity_type: this.entityType, entity_id: id, items }, { show_pros: false }).then((res) => {
        if (res.data && res.data.error) this.$toast.open({ message: res.data.msg || this.L.err, type: "error", position: "bottom-left" });
        return res.data;
      }).catch(() => {
        this.$toast.open({ message: this.L.err, type: "error", position: "bottom-left" });
        return null;
      });
    },
    /** Append AI-generated FAQ pairs (caller confirms if items already exist). */
    mergeItems(list) {
      if (!Array.isArray(list) || !list.length) return;
      list.forEach((f) => {
        const q = String((f && f.q) || "").trim();
        const a = String((f && f.a) || "").trim();
        if (q && a && this.items.length < 50) this.items.push({ q, a });
      });
    },
  },
};
</script>

<style scoped lang="scss">
.faqbox { background: var(--surface, #fff); border: 1px solid var(--border, #e5e7eb); border-radius: 12px; color: var(--text, #111827); overflow: hidden; margin-bottom: 1rem; }
.faqbox__head { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; padding: 12px 16px; border-bottom: 1px solid var(--border, #e5e7eb);
  h3 { margin: 0; font-size: 15px; font-weight: 700; } small { color: var(--muted, #6b7280); font-weight: 400; } }
.faqbox__hint { font-size: 12px; color: var(--muted, #6b7280); }
.faqbox__body { padding: 12px 16px; display: grid; gap: 10px; }
.faqbox__item { display: grid; gap: 6px; padding: 10px; border: 1px dashed var(--border-strong, #d1d5db); border-radius: 8px; }
.faqbox__bar { display: flex; justify-content: space-between; align-items: center; }
.faqbox__num { font-size: 12px; color: var(--muted, #6b7280); }
.faqbox__icon { border: 1px solid var(--border, #e5e7eb); background: transparent; color: inherit; border-radius: 6px; width: 28px; height: 26px; margin-inline-start: 4px; cursor: pointer;
  &:disabled { opacity: .35; cursor: default; } &--del { color: var(--danger, #dc2626); } }
.faqbox__btn { justify-self: start; border: 1px solid var(--accent, #2563eb); color: var(--accent, #2563eb); background: transparent; border-radius: 8px; padding: 5px 12px; font-size: 13px; cursor: pointer; }
</style>
